// ABOUTME: The ContentDraft store — the single client-side copy of a business's
// drafts. Fetches `GET /content/drafts` and owns every draft mutation the GUI
// still performs: edit, schedule, publish, delete. Named useDraftsStore until
// #1935 deleted the kanban pipeline page along with its filter/selection state;
// the draft CRUD it wrapped is what the unified /content page and its detail
// view run on, so the store survived the page.

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { ContentDraft, ContentDraftStatus } from '@/types'
import { api, isApiError } from '@/api/client'

interface DraftsState {
  drafts: ContentDraft[]
  loading: boolean
  error: string | null
}

// Body of DELETE /content/drafts/{id} — snake_case, straight off
// `DraftDeletionResult.as_dict()` (app/services/content/draft_deletion.py).
export interface DraftDeletionResponse {
  draft_id: string | null
  outcome: string
  deleted: boolean
  message: string
  platforms: {
    platform: string
    external_id: string | null
    deleted: boolean
    unsupported: boolean
    error: string | null
  }[]
}

export type LiveEditTarget = 'caption' | 'title' | 'thumbnail'

// Body of POST /content/drafts/{id}/live-edit — `LivePostEditResult.as_dict()`
// (app/services/content/live_edit/service.py). A 422 carries the reasons when no
// platform took the change.
export interface LiveEditResponse {
  draft_id: string
  target: LiveEditTarget
  outcome: 'edited' | 'partial'
  message: string
  platforms: {
    platform: string
    edited: boolean
    external_id: string | null
    unsupported: boolean
    error: string | null
    advice: string | null
    alternatives: string[]
  }[]
}

// Subset of ContentDraft fields the draft detail view can update.
// Backend `PATCH /content/drafts/{id}` accepts arbitrary partial updates;
// we constrain the client surface to fields the modal actually edits so
// callers can't accidentally clobber generation_params, version, etc.
export interface DraftPatch {
  title?: string | null
  format?: string
  body?: string | null
  scheduled_at?: string | null
  platforms?: string[]
  tags?: string[]
  // Multi-profile publish binding (issue #1226): platform -> ConnectedAccount id.
  connected_account_ids?: Record<string, string> | null
  // AI image edit/regenerate + undo (issue #1454). PATCHing media_urls persists
  // an undo (restore the previous image); the forward edit is persisted
  // server-side by the edit-region endpoint and reflected locally via
  // applyDraftMedia.
  media_urls?: string[]
  // AI-edit undo (issue #1947). An ad's headline/description/CTA and a GMB
  // button live in platform_settings, so restoring the edit snapshot without
  // this key silently restores nothing for those categories. The backend
  // merges it at the top level (`_MERGED_JSONB_FIELDS`), so PATCHing the
  // snapshot's dict puts the whole per-platform sub-dict back.
  platform_settings?: Record<string, unknown>
}

interface DraftsActions {
  fetchDrafts: (opts?: { silent?: boolean }) => Promise<void>
  updateDraft: (id: string, patch: DraftPatch) => Promise<void>
  // Reflect a server-side media change (AI image edit/regenerate) locally,
  // without a PATCH — the backend already persisted the new media_urls.
  applyDraftMedia: (id: string, mediaUrls: string[]) => void
  deleteDraft: (id: string) => Promise<void>
  // Change a live post's caption or title on its platforms, then on the draft.
  editLivePost: (id: string, text: string, target: LiveEditTarget) => Promise<LiveEditResponse>
  scheduleDraft: (id: string, scheduledAt: string) => Promise<void>
  cancelSchedule: (id: string) => Promise<void>
  // Resolves with the server's post-publish state: inside the grace window the
  // draft is SCHEDULED with a near-future scheduled_at, and callers (the chat
  // reply) must describe it as queued-and-cancelable, never as published.
  publishDraft: (id: string) => Promise<{ status: ContentDraftStatus; scheduled_at: string | null }>
  clearError: () => void
}

type DraftsStore = DraftsState & DraftsActions

const initialState: DraftsState = {
  drafts: [],
  loading: false,
  error: null,
}

/**
 * Hard ceiling on how long one content read may stay in flight. `loading`
 * gates the skeleton-vs-empty-state branch on /content, so a request that
 * never settles (a stalled edge connection) would leave the page on skeletons
 * forever (#2201). Aborting turns the hang into a settled failure the store's
 * normal error handling already surfaces with "Try again".
 */
export const CONTENT_FETCH_TIMEOUT_MS = 15_000

export const useDraftsStore = create<DraftsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchDrafts: async (opts?: { silent?: boolean }) => {
        // Silent refetches (background polling, focus-regain) skip the
        // loading flag so the grid doesn't flip to skeleton + back every 15s,
        // which causes cards to visibly disappear/reappear.
        const silent = !!opts?.silent
        if (!silent) set({ loading: true, error: null })
        try {
          const drafts = await api.get<ContentDraft[]>('/content/drafts', {
            params: { limit: 100, offset: 0 },
            signal: AbortSignal.timeout(CONTENT_FETCH_TIMEOUT_MS),
          })
          set({ drafts: Array.isArray(drafts) ? drafts : [], loading: false })
        } catch (error) {
          // Silent failures (transient polling blips) shouldn't surface a
          // banner — log and move on. User-initiated fetches keep the
          // error so the UI can show "Try again".
          if (silent) {
            console.warn('Background draft poll failed:', error)
            set({ loading: false })
            return
          }
          const message = isApiError(error) ? error.message : 'Failed to fetch content drafts'
          set({ error: message, loading: false })
        }
      },

      updateDraft: async (id: string, patch: DraftPatch) => {
        // Generic partial update for the detail view — title/body/platforms.
        // Optimistic local update, PATCH, roll back on error. Scheduling goes
        // through scheduleDraft so the status/scheduled_at coupling stays in
        // one place.
        const { drafts } = get()
        const draft = drafts.find((d) => d.id === id)
        if (!draft) return

        const previous = {
          title: draft.title,
          body: draft.body,
          scheduled_at: draft.scheduled_at,
          platforms: draft.platforms,
          connected_account_ids: draft.connected_account_ids,
          media_urls: draft.media_urls,
          platform_settings: draft.platform_settings,
        }

        set({
          drafts: drafts.map((d) => (d.id === id ? { ...d, ...patch } : d)),
        })

        try {
          await api.patch(`/content/drafts/${id}`, patch)
        } catch (error) {
          set({
            drafts: get().drafts.map((d) => (d.id === id ? { ...d, ...previous } : d)),
            error: isApiError(error) ? error.message : 'Failed to update draft',
          })
          throw error
        }
      },

      applyDraftMedia: (id: string, mediaUrls: string[]) => {
        set({
          drafts: get().drafts.map((d) => (d.id === id ? { ...d, media_urls: mediaUrls } : d)),
        })
      },

      deleteDraft: async (id: string) => {
        // DELETE /content/drafts/{id} takes any LIVE platform post down first,
        // then decides the local row's fate (#2190):
        //   deleted: true  -> nothing was live; the row is gone for good.
        //   deleted: false -> every live post was removed from its platform and
        //                     the row survives as `removed` so the lead
        //                     attribution it earned is not rewritten.
        // A platform we could not remove is a 400 — the post is still live, so
        // the optimistic removal rolls back and the error says which platform.
        // A PARTIAL takedown is also a 400, but the server row is now
        // `partially_removed` (#2218) — refetch so the rollback doesn't leave
        // a half-dead post reading `published` in the grid.
        const { drafts } = get()
        const draft = drafts.find((d) => d.id === id)
        if (!draft) return
        const previous = drafts

        set({ drafts: drafts.filter((d) => d.id !== id) })

        try {
          // ApiResponse<T> IS T at runtime — the body is not wrapped.
          const result = await api.delete<DraftDeletionResponse>(`/content/drafts/${id}`)
          if (result?.deleted === false) {
            set({
              drafts: previous.map((d) =>
                d.id === id ? { ...d, status: 'removed' as ContentDraftStatus } : d
              ),
            })
          }
        } catch (error) {
          set({
            drafts: previous,
            error: isApiError(error) ? error.message : 'Failed to delete draft',
          })
          void get().fetchDrafts({ silent: true })
          throw error
        }
      },

      editLivePost: async (id: string, text: string, target: LiveEditTarget) => {
        // No optimistic update: only the platforms decide whether the live copy
        // changed. The stored text follows once at least one confirmed.
        const res = await api.post<LiveEditResponse>(`/content/drafts/${id}/live-edit`, {
          text,
          target,
        })
        const field = target === 'title' ? 'title' : 'body'
        set({
          drafts: get().drafts.map((d) => (d.id === id ? { ...d, [field]: text } : d)),
        })
        return res
      },

      scheduleDraft: async (id: string, scheduledAt: string) => {
        // Single PATCH that flips status to 'scheduled' AND sets the time.
        // The backend ARQ scheduler picks up the row and publishes when
        // scheduled_at hits. Without scheduled_at the publisher has
        // nothing to fire — sending status='scheduled' alone is broken.
        const { drafts } = get()
        const draft = drafts.find((d) => d.id === id)
        if (!draft) return
        const oldStatus = draft.status
        const oldScheduledAt = draft.scheduled_at

        set({
          drafts: drafts.map((d) =>
            d.id === id ? { ...d, status: 'scheduled', scheduled_at: scheduledAt } : d
          ),
        })

        try {
          await api.patch(`/content/drafts/${id}`, {
            status: 'scheduled',
            scheduled_at: scheduledAt,
          })
        } catch (error) {
          set({
            drafts: get().drafts.map((d) =>
              d.id === id ? { ...d, status: oldStatus, scheduled_at: oldScheduledAt } : d
            ),
            error: isApiError(error) ? error.message : 'Failed to schedule draft',
          })
          throw error
        }
      },

      cancelSchedule: async (id: string) => {
        // Return a scheduled post to drafts. Third surface for a capability MCP
        // and chat already had (#2008, AC-10) — and what makes the publish-now
        // grace window meaningful from the dashboard. No optimistic update: the
        // server owns the state-machine decision (it refuses once PUBLISHING),
        // so showing 'draft' before it answers would lie half the time.
        const res = await api.post<{
          status: ContentDraftStatus
          scheduled_at: string | null
        }>(`/content/drafts/${id}/cancel-schedule`)
        set({
          drafts: get().drafts.map((d) =>
            d.id === id ? { ...d, status: res.status, scheduled_at: res.scheduled_at } : d
          ),
        })
      },

      publishDraft: async (id: string) => {
        // Real publish-now: hits the dedicated endpoint that books the draft
        // behind the cancelable grace window (#2008, AC-10) and lets the publish
        // cron fan it out. This is distinct from scheduleDraft — scheduling
        // takes a user-chosen future time; publish-now takes the window.
        const { drafts } = get()
        const draft = drafts.find((d) => d.id === id)
        const oldStatus = draft?.status
        const oldScheduledAt = draft?.scheduled_at

        set({
          drafts: get().drafts.map((d) =>
            d.id === id ? { ...d, status: 'publishing' as ContentDraftStatus } : d
          ),
        })

        try {
          const res = await api.post<{
            status: ContentDraftStatus
            scheduled_at: string | null
          }>(`/content/drafts/${id}/publish`)
          set({
            drafts: get().drafts.map((d) =>
              d.id === id ? { ...d, status: res.status, scheduled_at: res.scheduled_at } : d
            ),
          })
          return { status: res.status, scheduled_at: res.scheduled_at }
        } catch (error) {
          set({
            drafts: get().drafts.map((d) =>
              d.id === id && oldStatus
                ? { ...d, status: oldStatus, scheduled_at: oldScheduledAt ?? null }
                : d
            ),
            error: isApiError(error) ? error.message : 'Failed to publish draft',
          })
          throw error
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    { name: 'DraftsStore', enabled: import.meta.env.DEV }
  )
)

// Selectors
export const selectDrafts = (state: DraftsStore) => state.drafts
export const selectDraftsLoading = (state: DraftsStore) => state.loading
export const selectDraftsError = (state: DraftsStore) => state.error
